import React, { useState, useEffect, useRef } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { CalendarIcon, ArrowLongRightIcon } from '@heroicons/react/20/solid';
import { cn } from '../../../lib/utils.ts';
import { fieldVariants } from '../fieldStyles.ts';

import {
  formatDateForInput,
  parseDate,
  getYearRange,
  getQuarterRange,
  getMonthRange,
  getPresetRange,
  isSameDay,
  isValidParsedDate,
  determineRangeInfo,
} from './utils/dateRangeUtils';
import { TABS, DateRangeTab, DateRangePreset } from './constants';
import { YearTabPanel, QuarterTabPanel, MonthTabPanel, CustomTabPanel } from './DateRangeTabPanels';

const PANEL_ANCHOR_GAP = 8;

export type DateRangeValue = {
  from: Date | null;
  to: Date | null;
};

export type DateRangeInputProps = {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  className?: string;
  defaultPreset?: DateRangePreset;
  disabled?: boolean;
};

export const DateRangeInput: React.FC<DateRangeInputProps> = ({ value, onChange, className = "", defaultPreset, disabled = false }) => {
  const onRangeChangeRef = useRef(onChange);
  onRangeChangeRef.current = onChange;

  const [{ tab: initialTab, year: initialYear }] = useState(() => determineRangeInfo(value));
  
  const [activeTab, setActiveTab] = useState<DateRangeTab>(initialTab);
  const [selectedYear, setSelectedYear] = useState<number>(initialYear);
  const [fromInputValue, setFromInputValue] = useState<string>(formatDateForInput(value.from));
  const [toInputValue, setToInputValue] = useState<string>(formatDateForInput(value.to));

  useEffect(() => {
    if (value.from != null || value.to != null || defaultPreset == null) {
      return;
    }
    onRangeChangeRef.current(getPresetRange(defaultPreset));
  }, [defaultPreset, value.from, value.to]);

  useEffect(() => {
    const { tab, year } = determineRangeInfo(value);
    setActiveTab(tab);
    setSelectedYear(year);
  }, [value.from, value.to]);

  useEffect(() => {
    setFromInputValue(formatDateForInput(value.from));
  }, [value.from]);

  useEffect(() => {
    setToInputValue(formatDateForInput(value.to));
  }, [value.to]);

  const clearDateField = (field: 'from' | 'to', currentFieldValue: Date | null) => {
    if (currentFieldValue == null) return;
    onChange({ ...value, [field]: null });
  };

  const commitDateField = (field: 'from' | 'to', rawInputValue: string) => {
    const parsedDate = parseDate(rawInputValue);
    const currentFieldValue = field === 'from' ? value.from : value.to;
    const setInputValue = field === 'from' ? setFromInputValue : setToInputValue;

    if (rawInputValue === '') {
      clearDateField(field, currentFieldValue);
      return;
    }

    if (!isValidParsedDate(parsedDate)) {
      setInputValue(formatDateForInput(currentFieldValue));
      return;
    }

    if (isSameDay(parsedDate, currentFieldValue)) {
      return;
    }

    setSelectedYear(parsedDate.getFullYear());
    onChange({ ...value, [field]: parsedDate });
  };

  const applyYearRange = (year: number) => {
    onChange(getYearRange(year));
  };

  const applyQuarterRange = (year: number, quarter: number) => {
    onChange(getQuarterRange(year, quarter));
  };

  const applyMonthRange = (year: number, month: number) => {
    onChange(getMonthRange(year, month));
  };

  return (
    <Popover
      className={cn(
        fieldVariants(),
        "relative flex items-center justify-between transition-all duration-200",
        disabled && "cursor-not-allowed bg-gray-50 opacity-60",
        className
      )}
    >
      <div className="flex-1 flex items-center overflow-hidden">
        <div className="flex items-center  w-full">
          {/* Firefox has no pseudo-element for its calendar button, so crop it off instead */}
          <div className="flex-1 min-w-0 overflow-hidden">
            <input
              type="date"
              name="from"
              disabled={disabled}
              className="w-full supports-[-moz-appearance:none]:w-[calc(100%+1.5rem)] bg-transparent outline-none text-sm supports-[-moz-appearance:none]:text-[13px] text-foreground appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-clear-button]:appearance-none  border-none mt-0.5"
              value={fromInputValue}
              onChange={(e) => setFromInputValue(e.target.value)}
              onBlur={(e) => commitDateField('from', e.target.value)}
            />
          </div>
          <ArrowLongRightIcon className="w-4 h-4 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0 overflow-hidden pl-2">
            <input
              type="date"
              name="to"
              disabled={disabled}
              className="w-full supports-[-moz-appearance:none]:w-[calc(100%+1.5rem)] bg-transparent outline-none text-sm supports-[-moz-appearance:none]:text-[13px] text-foreground appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-clear-button]:appearance-none  border-none mt-0.5"
              value={toInputValue}
              onChange={(e) => setToInputValue(e.target.value)}
              onBlur={(e) => commitDateField('to', e.target.value)}
            />
          </div>
        </div>
      </div>

      <PopoverButton disabled={disabled} className="
        flex-shrink-0 flex items-center gap-1 px-1 text-xs font-medium 
        text-muted-foreground hover:text-primary transition-colors outline-none mt-0 
      ">
        <CalendarIcon className="w-4 h-4 text-muted-foreground" />
      </PopoverButton>

      <PopoverPanel
        anchor={{ to: 'bottom start', gap: PANEL_ANCHOR_GAP }}
        className="z-50 bg-popover border border-border rounded-xl shadow-xl flex overflow-hidden min-w-[360px]"
      >
        {({ close }) => (
          <>
            <div className="w-28 bg-muted border-r border-border p-2 space-y-1">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`
                  w-full text-left px-3 py-2 text-sm rounded-lg transition-colors
                  ${activeTab === tab ? 'bg-popover border border-border shadow-sm font-medium text-primary' : 'text-muted-foreground hover:bg-accent'}
                `}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="flex-1 bg-popover overflow-hidden flex flex-col h-[300px] w-64">
              {activeTab === DateRangeTab.Year && (
                <YearTabPanel
                  value={value}
                  onApplyYear={(year) => {
                    applyYearRange(year);
                    close();
                  }}
                />
              )}
              {activeTab === DateRangeTab.Quarter && (
                <QuarterTabPanel
                  value={value}
                  selectedYear={selectedYear}
                  onYearSelect={setSelectedYear}
                  onApplyQuarter={(year, quarter) => {
                    applyQuarterRange(year, quarter);
                    close();
                  }}
                />
              )}
              {activeTab === DateRangeTab.Month && (
                <MonthTabPanel
                  value={value}
                  selectedYear={selectedYear}
                  onYearSelect={setSelectedYear}
                  onApplyMonth={(year, month) => {
                    applyMonthRange(year, month);
                    close();
                  }}
                />
              )}
              {activeTab === DateRangeTab.Custom && (
                <CustomTabPanel
                  fromInputValue={fromInputValue}
                  toInputValue={toInputValue}
                  onFromInputChange={setFromInputValue}
                  onToInputChange={setToInputValue}
                  onFromInputBlur={(rawValue) => commitDateField('from', rawValue)}
                  onToInputBlur={(rawValue) => commitDateField('to', rawValue)}
                />
              )}
            </div>
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
};
